import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import ImageModal from "../components/ImageModal";

function StoreHourly() {
    const { siteId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [modalImages, setModalImages] = useState([]);
    const [modalIndex, setModalIndex] = useState(0);
    const [viewMode, setViewMode] = useState("time"); // "time" or "cam"
    const [expandedSections, setExpandedSections] = useState(new Set());

    useEffect(() => {
        setLoading(true);
        setExpandedSections(new Set());
        api.hourlyImages(siteId, date)
            .then((d) => {
                if (d.times.length === 0 && d.availableDates && d.availableDates.length > 0 && date === new Date().toISOString().slice(0, 10)) {
                    setDate(d.availableDates[0]);
                    return;
                }
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [siteId, date]);

    const toggleSection = (key) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const expandAll = () => {
        if (viewMode === "time") {
            setExpandedSections(new Set((data?.times || []).map((t) => t.time)));
        } else {
            setExpandedSections(new Set(cameraNumbers.map((c) => `cam-${c}`)));
        }
    };

    const collapseAll = () => setExpandedSections(new Set());

    const openModal = (images, startIndex) => {
        setModalImages(images);
        setModalIndex(startIndex);
    };

    // Flatten all images for "by time" modal navigation
    const allImagesByTime = useMemo(() => {
        if (!data) return [];
        return data.times.flatMap((t) =>
            t.cameras.map((c) => ({
                url: c.url,
                filename: c.filename,
                zone: `Camera ${c.camera}`,
                capturedAt: `${date} ${t.displayTime}`,
            }))
        );
    }, [data, date]);

    // Build camera-grouped data for "by cam" view
    const cameraNumbers = useMemo(() => {
        if (!data) return [];
        const camSet = new Set();
        data.times.forEach((t) => t.cameras.forEach((c) => camSet.add(c.camera)));
        return [...camSet].sort((a, b) => a - b);
    }, [data]);

    const cameraData = useMemo(() => {
        if (!data) return {};
        const map = {};
        data.times.forEach((t) => {
            t.cameras.forEach((c) => {
                if (!map[c.camera]) map[c.camera] = [];
                map[c.camera].push({
                    time: t.time,
                    displayTime: t.displayTime,
                    url: c.url,
                    filename: c.filename,
                    size: c.size,
                });
            });
        });
        return map;
    }, [data]);

    // Build flat images for "by cam" modal navigation per camera
    const camModalImages = (camNum) => {
        return (cameraData[camNum] || []).map((img) => ({
            url: img.url,
            filename: img.filename,
            zone: `Camera ${camNum}`,
            capturedAt: `${date} ${img.displayTime}`,
        }));
    };

    return (
        <div>
            {/* Header */}
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <Link to="/stores" style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>
                        Stores
                    </Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <Link to={`/store/${siteId}`} style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>
                        {siteId}
                    </Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <span style={{ fontSize: 13 }}>Hourly Images</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <h2>Hourly Images {"\u2014"} Site {siteId}</h2>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* View mode toggle */}
                        <div style={{ display: "flex", gap: 0 }}>
                            {[
                                { key: "time", label: "By Time" },
                                { key: "cam", label: "By Camera" },
                            ].map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => { setViewMode(m.key); setExpandedSections(new Set()); }}
                                    style={{
                                        padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                        border: "1px solid var(--border)",
                                        borderRight: m.key === "time" ? "none" : undefined,
                                        borderRadius: m.key === "time" ? "6px 0 0 6px" : "0 6px 6px 0",
                                        background: viewMode === m.key ? "var(--primary)" : "var(--bg-hover)",
                                        color: viewMode === m.key ? "#fff" : "var(--text-secondary)",
                                    }}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        {/* Expand/collapse */}
                        {!loading && data && data.times.length > 0 && (
                            <div style={{ display: "flex", gap: 4 }}>
                                <button onClick={expandAll} style={btnSmall}>Expand All</button>
                                <button onClick={collapseAll} style={btnSmall}>Collapse</button>
                            </div>
                        )}
                    </div>
                </div>
                {data && <p>{data.totalImages} images on {date} {"\u2014"} {cameraNumbers.length} cameras, {data.times.length} time slots</p>}
            </div>

            {/* Date selector bar - prominent on top */}
            {data && data.availableDates && data.availableDates.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="filter-select"
                        style={{ fontSize: 14, padding: "6px 12px" }}
                    />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {data.availableDates.map((d) => (
                            <button
                                key={d}
                                onClick={() => setDate(d)}
                                style={{
                                    fontSize: 12, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                                    border: d === date ? "1px solid var(--primary)" : "1px solid var(--border)",
                                    background: d === date ? "var(--primary)" : "var(--bg-hover)",
                                    color: d === date ? "#fff" : "var(--text-secondary)",
                                    fontWeight: d === date ? 700 : 400,
                                }}
                            >
                                {formatDateShort(d)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {loading && <LoadingSpinner message="Loading images..." />}

            {!loading && data && data.times.length === 0 && (
                <div className="card" style={{ padding: 40, textAlign: "center" }}>
                    <h3 style={{ color: "var(--text-secondary)" }}>No images for {date}</h3>
                    <p style={{ color: "var(--text-muted)" }}>
                        Try selecting a different date.
                        {data.availableDates && data.availableDates.length > 0 && (
                            <span> Available: {data.availableDates.slice(0, 5).join(", ")}</span>
                        )}
                    </p>
                </div>
            )}

            {/* BY TIME view */}
            {!loading && data && viewMode === "time" && data.times.map((slot) => {
                const isExpanded = expandedSections.has(slot.time);
                let offset = 0;
                for (const t of data.times) {
                    if (t.time === slot.time) break;
                    offset += t.cameras.length;
                }

                return (
                    <div key={slot.time} className="card" style={{ marginBottom: 2, overflow: "hidden" }}>
                        <div
                            onClick={() => toggleSection(slot.time)}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "10px 16px", cursor: "pointer",
                                background: isExpanded ? "var(--bg-hover)" : "transparent",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 10, color: "var(--text-muted)", width: 12 }}>
                                    {isExpanded ? "\u25BC" : "\u25B6"}
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 700 }}>{slot.displayTime}</span>
                                <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                                    {slot.cameras.length} cameras
                                </span>
                            </div>
                            {/* Mini preview strip when collapsed */}
                            {!isExpanded && (
                                <div style={{ display: "flex", gap: 3 }}>
                                    {slot.cameras.slice(0, 8).map((cam) => (
                                        <div key={cam.camera} style={{
                                            width: 32, height: 22, borderRadius: 3, overflow: "hidden",
                                            border: "1px solid var(--border)",
                                        }}>
                                            <img src={cam.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                        </div>
                                    ))}
                                    {slot.cameras.length > 8 && (
                                        <span style={{ fontSize: 11, color: "var(--text-muted)", alignSelf: "center" }}>
                                            +{slot.cameras.length - 8}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {isExpanded && (
                            <div style={{ padding: "8px 16px 14px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {slot.cameras.map((cam, i) => (
                                    <div
                                        key={cam.camera}
                                        onClick={() => openModal(allImagesByTime, offset + i)}
                                        style={{
                                            cursor: "pointer", position: "relative",
                                            borderRadius: 6, overflow: "hidden",
                                            border: "1px solid var(--border)",
                                        }}
                                    >
                                        <img
                                            src={cam.url}
                                            alt={`Camera ${cam.camera}`}
                                            style={{ width: 180, height: 120, objectFit: "cover", display: "block" }}
                                            loading="lazy"
                                        />
                                        <div style={thumbLabel}>Cam {cam.camera}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* BY CAMERA view */}
            {!loading && data && viewMode === "cam" && cameraNumbers.map((camNum) => {
                const key = `cam-${camNum}`;
                const isExpanded = expandedSections.has(key);
                const imgs = cameraData[camNum] || [];

                return (
                    <div key={key} className="card" style={{ marginBottom: 2, overflow: "hidden" }}>
                        <div
                            onClick={() => toggleSection(key)}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "10px 16px", cursor: "pointer",
                                background: isExpanded ? "var(--bg-hover)" : "transparent",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 10, color: "var(--text-muted)", width: 12 }}>
                                    {isExpanded ? "\u25BC" : "\u25B6"}
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 700 }}>Camera {camNum}</span>
                                <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                                    {imgs.length} captures
                                </span>
                            </div>
                            {/* Mini preview strip when collapsed */}
                            {!isExpanded && (
                                <div style={{ display: "flex", gap: 3 }}>
                                    {imgs.filter((_, i) => i % Math.max(1, Math.floor(imgs.length / 6)) === 0).slice(0, 6).map((img, i) => (
                                        <div key={i} style={{
                                            width: 32, height: 22, borderRadius: 3, overflow: "hidden",
                                            border: "1px solid var(--border)",
                                        }}>
                                            <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {isExpanded && (
                            <div style={{ padding: "8px 16px 14px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {imgs.map((img, i) => (
                                    <div
                                        key={img.time}
                                        onClick={() => openModal(camModalImages(camNum), i)}
                                        style={{
                                            cursor: "pointer", position: "relative",
                                            borderRadius: 6, overflow: "hidden",
                                            border: "1px solid var(--border)",
                                        }}
                                    >
                                        <img
                                            src={img.url}
                                            alt={`Cam ${camNum} ${img.displayTime}`}
                                            style={{ width: 180, height: 120, objectFit: "cover", display: "block" }}
                                            loading="lazy"
                                        />
                                        <div style={thumbLabel}>{img.displayTime}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {modalImages.length > 0 && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages([])}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

const btnSmall = {
    fontSize: 11, padding: "4px 10px", borderRadius: 5, cursor: "pointer",
    border: "1px solid var(--border)", background: "var(--bg-hover)",
    color: "var(--text-secondary)",
};

const thumbLabel = {
    position: "absolute", bottom: 0, left: 0, right: 0,
    background: "rgba(0,0,0,0.7)", padding: "3px 8px",
    fontSize: 11, color: "#fff", fontWeight: 600,
};

function formatDateShort(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return `${days[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}

export default StoreHourly;
